import { useMemo, useState } from "react";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Button } from "../components/duvi/Button";
import { StatusDot } from "../components/duvi/StatusDot";
import { ChevronLeft, ChevronRight, Network, Search } from "lucide-react";
import { niches, type NicheSummary } from "../api/client";
import { useQuery } from "../hooks/useApi";
import { testIds } from "@duvi-agency/ui";

type StatusDotVariant = "active" | "warning" | "error" | "inactive";

// Map NicheSummary.status (contract enum) → StatusDot variant
function getStatusDotVariant(status: NicheSummary["status"]): StatusDotVariant {
  switch (status) {
    case "active":
      return "active";
    case "provisioning":
      return "warning";
    case "suspended":
      return "error";
    case "tearing_down":
      return "inactive";
    default:
      return "inactive";
  }
}

function getStatusLabel(status: NicheSummary["status"]): string {
  switch (status) {
    case "active":
      return "Active";
    case "provisioning":
      return "Provisioning";
    case "suspended":
      return "Suspended";
    case "tearing_down":
      return "Tearing down";
    default:
      return status;
  }
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const PAGE_SIZE = 50;

export function NichesList() {
  const [search, setSearch] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  // Stack of previously-seen cursors so the Previous button can step back.
  const [cursorStack, setCursorStack] = useState<Array<string | undefined>>([]);

  const {
    data: nichesData,
    meta,
    isLoading,
    error,
  } = useQuery(() => niches.list({ cursor, limit: PAGE_SIZE }), [cursor]);

  const allNiches: NicheSummary[] = nichesData ?? [];

  // Client-side search across name, short_code, and domain.
  const filteredNiches = useMemo(() => {
    if (!search.trim()) return allNiches;
    const q = search.trim().toLowerCase();
    return allNiches.filter(
      (n) =>
        n.name.toLowerCase().includes(q) ||
        n.short_code.toLowerCase().includes(q) ||
        n.domain.toLowerCase().includes(q),
    );
  }, [allNiches, search]);

  const nextCursor = meta?.cursor ?? null;
  const hasNext = Boolean(nextCursor);
  const hasPrev = cursorStack.length > 0;

  const handleNext = () => {
    if (!hasNext || !nextCursor) return;
    setCursorStack((s) => [...s, cursor]);
    setCursor(nextCursor);
  };

  const handlePrev = () => {
    if (!hasPrev) return;
    setCursorStack((s) => {
      const next = s.slice(0, -1);
      const previousCursor = s[s.length - 1];
      setCursor(previousCursor);
      return next;
    });
  };

  const EmptyState = () => (
    <div
      data-testid={testIds.nicheEmpty}
      className="flex flex-col items-center justify-center py-16"
    >
      <Network
        className="w-16 h-16 text-duvi-text-secondary mb-6"
        strokeWidth={1}
      />
      <h2
        className="text-duvi-text-primary mb-2"
        style={{
          fontSize: "var(--text-lg)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        {search.trim() ? "No niches match your search" : "No niches yet"}
      </h2>
      <p
        className="text-duvi-text-secondary"
        style={{ fontSize: "var(--text-sm)" }}
      >
        {search.trim()
          ? "Try a different name, short code, or domain."
          : "Niches will appear here once they are provisioned in the registry."}
      </p>
    </div>
  );

  return (
    <AppShell breadcrumbs={["Platform", "Niches"]} activeMenuItem="Niches">
      <div data-testid={testIds.nichesPage} className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1
              className="text-duvi-text-primary mb-2"
              style={{
                fontSize: "var(--text-xl)",
                fontWeight: "var(--font-weight-semibold)",
                lineHeight: "var(--line-height-heading)",
              }}
            >
              Niches
            </h1>
            <p
              className="text-duvi-text-secondary"
              style={{ fontSize: "var(--text-sm)" }}
            >
              Provisioned niche platforms from the Terragrunt registry
            </p>
          </div>
        </div>

        {/* Search */}
        <Card className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-duvi-text-secondary pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, short code, or domain"
              data-testid={testIds.nicheSearch}
              className="w-full pl-9 pr-3 py-2 bg-duvi-bg border border-duvi-border rounded-[6px] text-duvi-text-primary focus:outline-none focus:border-duvi-accent transition-colors"
              style={{ fontSize: "var(--text-sm)" }}
              aria-label="Search niches"
            />
          </div>
        </Card>

        {/* Table */}
        {isLoading ? (
          <Card className="p-6">
            <div className="flex items-center justify-center py-16">
              <div
                className="text-duvi-text-secondary"
                style={{ fontSize: "var(--text-sm)" }}
              >
                Loading niches...
              </div>
            </div>
          </Card>
        ) : error ? (
          <Card className="p-6">
            <div className="flex items-center justify-center py-16">
              <div
                className="text-duvi-destructive"
                style={{ fontSize: "var(--text-sm)" }}
              >
                Failed to load niches: {error.message}
              </div>
            </div>
          </Card>
        ) : filteredNiches.length === 0 ? (
          <Card className="p-6">
            <EmptyState />
          </Card>
        ) : (
          <>
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-duvi-border">
                    <tr>
                      <th
                        className="text-left p-4"
                        style={{
                          fontSize: "var(--text-xs)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        <span className="text-duvi-text-secondary">Status</span>
                      </th>
                      <th
                        className="text-left p-4"
                        style={{
                          fontSize: "var(--text-xs)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        <span className="text-duvi-text-secondary">Name</span>
                      </th>
                      <th
                        className="text-left p-4"
                        style={{
                          fontSize: "var(--text-xs)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        <span className="text-duvi-text-secondary">Domain</span>
                      </th>
                      <th
                        className="text-left p-4"
                        style={{
                          fontSize: "var(--text-xs)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        <span className="text-duvi-text-secondary">
                          Short code
                        </span>
                      </th>
                      <th
                        className="text-left p-4"
                        style={{
                          fontSize: "var(--text-xs)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        <span className="text-duvi-text-secondary">
                          Accounts
                        </span>
                      </th>
                      <th
                        className="text-left p-4"
                        style={{
                          fontSize: "var(--text-xs)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        <span className="text-duvi-text-secondary">
                          Created
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNiches.map((niche) => {
                      const accountEnvs = (
                        ["dev", "stg", "prd", "shared"] as const
                      ).filter((env) => Boolean(niche.account_ids?.[env]));
                      return (
                        <tr
                          key={niche.id}
                          data-testid={testIds.nicheRow}
                          className="border-b border-duvi-border hover:bg-duvi-elevated/50 transition-colors"
                        >
                          <td className="p-4">
                            <div
                              className="flex items-center gap-2"
                              title={getStatusLabel(niche.status)}
                            >
                              <StatusDot
                                status={getStatusDotVariant(niche.status)}
                              />
                              <span
                                className="text-duvi-text-secondary"
                                style={{ fontSize: "var(--text-xs)" }}
                              >
                                {getStatusLabel(niche.status)}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div
                              className="text-duvi-text-primary"
                              style={{
                                fontSize: "var(--text-sm)",
                                fontWeight: "var(--font-weight-semibold)",
                              }}
                            >
                              {niche.name}
                            </div>
                          </td>
                          <td className="p-4">
                            <span
                              className="text-duvi-text-secondary font-mono"
                              style={{ fontSize: "var(--text-xs)" }}
                            >
                              {niche.domain}
                            </span>
                          </td>
                          <td className="p-4">
                            <span
                              className="inline-block px-2 py-1 bg-duvi-surface rounded text-duvi-text-primary font-mono"
                              style={{ fontSize: "var(--text-xs)" }}
                            >
                              {niche.short_code}
                            </span>
                          </td>
                          <td className="p-4">
                            <span
                              className="text-duvi-text-secondary"
                              style={{ fontSize: "var(--text-xs)" }}
                            >
                              {accountEnvs.length > 0
                                ? accountEnvs.join(", ")
                                : "—"}
                            </span>
                          </td>
                          <td className="p-4">
                            <span
                              className="text-duvi-text-secondary"
                              style={{ fontSize: "var(--text-xs)" }}
                            >
                              {formatRelativeDate(niche.created_at)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <div
                data-testid={testIds.nichePaginationInfo}
                className="text-duvi-text-secondary"
                style={{ fontSize: "var(--text-xs)" }}
              >
                Showing {filteredNiches.length} of {allNiches.length}{" "}
                {allNiches.length === 1 ? "niche" : "niches"}
                {search.trim() ? " (filtered)" : ""}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handlePrev}
                  disabled={!hasPrev}
                  data-testid={testIds.nichePaginationPrev}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleNext}
                  disabled={!hasNext}
                  data-testid={testIds.nichePaginationNext}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
